import { useSelector } from '@xstate/react';
import classNames from 'classnames';
import { UserContextV2 } from '~/app';
import { LoadingSpinner } from '~/components';

import { Layout } from '~/components/Layout';
import { PROVIDER_ROLE_CODE } from '~/features/user/constants';
import { VaultNewRecordsProvider } from '~/features/vault/newRecords/VaultNewRecordsProvider';
import { paths } from './routes';

interface VaultLayoutProps {
  children: React.ReactNode;
}

/**
 * Vault Layout Component
 *
 * Provides the consistent page layout for vault routes, including:
 * - Three panel layout (left nav, center content, right panel)
 * - Vault-specific left sidebar navigation
 * - Background styling
 *
 * Note: Provider data is fetched from vaultState's providerService
 * Header is handled separately by VaultHeader in the route structure
 */
export const VaultLayout = ({ children }: VaultLayoutProps) => {
  const UserContext = UserContextV2;
  const userRef = UserContext.useActorRef();
  const { vaultRef, roleCode } = useSelector(userRef, (state) => state.context);

  const isDelegating = useSelector(userRef, (state) =>
    state.matches({ $_AUTHENTICATED: '$_AUTHENTICATED_IMPERSONATING' })
  );

  if (!vaultRef) {
    return null;
  }

  const { provider } = useSelector(vaultRef, (state) => state.context);

  const Container = ({ children }: { children: React.ReactNode }) => {
    return <div className={classNames('bg-base', 'justify-center')}>{children}</div>;
  };

  const Loading = () => {
    return (
      <div className="flex justify-center items-center mt-20">
        <LoadingSpinner />
      </div>
    );
  };

  return (
    // [MCV-1812] Hosts the "New" marks for the side nav and every vault section. A delegated
    // employer sees them but their visit does not clear them for the provider.
    <VaultNewRecordsProvider providerId={provider?.id} readOnly={isDelegating}>
      <Layout
        allowBrandSwitch={!isDelegating}
        switchTargetPath={roleCode === PROVIDER_ROLE_CODE ? paths.hlx.jobOpportunities : undefined}
      >
        {provider ? (
          children
        ) : (
          <Container>
            <Loading />
          </Container>
        )}
      </Layout>
    </VaultNewRecordsProvider>
  );
};
